import { theme } from '@mi-front/ui';
import { alpha, createTheme } from '@mui/material';

export const muiTheme = createTheme({
  cssVariables: true,
  typography: {
    /** Uses the same variable as ui.css so FontSwitcher can change typography globally. */
    fontFamily: 'var(--font-family-sans-serif)',
  },
  palette: {
    primary: {
      main: '#1cc0fc',
      contrastText: '#fff',
    },
    secondary: {
      main: '#6c757d', // grey
      contrastText: '#fff',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.5)',
      disabled: 'rgba(0, 0, 0, 0.3)',
    },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiGrid: {
      defaultProps: {
        spacing: 2,
      },
    },
    MuiGrid2: {
      defaultProps: {
        spacing: 2,
      },
    },
    MuiStack: {
      defaultProps: {
        spacing: 2,
        useFlexGap: true,
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: '#03a6e2', // Darker variant for better contrast with white text
          '&:hover': {
            backgroundColor: '#0394c9', // Even darker on hover
          },
        },
        outlinedPrimary: {
          borderColor: '#03a6e2', // Use darker variant for border
          color: '#03a6e2', // Use darker variant for text
          '&:hover': {
            borderColor: '#0394c9',
            backgroundColor: 'rgba(3, 166, 226, 0.04)', // Light background on hover
          },
        },
        containedSuccess: {
          '&:hover': {
            backgroundColor: '#009966', // Slightly darker than success.main for hover
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        text: {
          fill: 'white',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          paddingTop: '8px',
          paddingBottom: '8px',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          paddingBottom: 16,
          '&:last-child': {
            paddingBottom: 16,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: '38px',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          '& fieldset': {
            borderColor: '#ced4da',
            borderRadius: '0.375rem',
          },
          '&:hover fieldset': {
            borderColor: '#ced4da',
          },
          '&.Mui-focused fieldset': {
            // borderColor: '#9ae3fe',
            // boxShadow: '0 0 0 0.2rem rgba(28, 192, 252, 0.25)',
          },
          // '&.Mui-disabled fieldset': {
          //   borderColor: '#ced4da',
          //   backgroundColor: '#e9ecef',
          // },
          '&.Mui-error fieldset': {
            borderColor: '#dc3545',
          },
          '&.Mui-error:hover fieldset': {
            borderColor: '#dc3545',
          },
          '&.Mui-error.Mui-focused fieldset': {
            borderColor: '#dc3545',
            // boxShadow: '0 0 0 0.2rem rgba(220, 53, 69, 0.25)',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          padding: '0rem 0.25rem',
          backgroundColor: '#ffffff',
        },
        input: {
          color: '#343a40',
          backgroundColor: '#ffffff',
          '&::placeholder': {
            color: '#808080',
            opacity: 1,
          },
        },
      },
    },
    MuiAutocomplete: {},
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover ::after': {
            backgroundColor: 'rgba(178, 178, 178, 0.15) !important',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          textTransform: 'none',
        },
      },
    },
    /** Filled variant uses getContrastText() instead of palette[severity].contrastText;
     * override so all filled Alerts use the theme contrast text for readability. */
    MuiAlert: {
      styleOverrides: {
        filledSuccess: ({ theme }) => ({
          color: theme.palette.success.contrastText,
          '& .MuiAlert-message, & .MuiAlert-message *': { color: 'inherit' },
        }),
        filledError: ({ theme }) => ({
          color: theme.palette.error.contrastText,
          '& .MuiAlert-message, & .MuiAlert-message *': { color: 'inherit' },
        }),
        filledWarning: ({ theme }) => ({
          color: theme.palette.warning.contrastText,
          '& .MuiAlert-message, & .MuiAlert-message *': { color: 'inherit' },
        }),
        filledInfo: ({ theme }) => ({
          color: theme.palette.info.contrastText,
          '& .MuiAlert-message, & .MuiAlert-message *': { color: 'inherit' },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        filledPrimary: {
          color: '#fff', // force white text
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {},
      },
    },
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          padding: 0,
          marginRight: 24,
          minWidth: 0,
          '@media (min-width: 600px)': {
            minWidth: 0, // Overrides the breakpoint-specific min-width
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: 0, // Globally normalize alignment
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography.body2,
          lineHeight: '36px',
          typography: 'caption',
        }),
      },
    },
    MuiFormHelperText: {
      defaultProps: {
        component: 'div',
      },
    },
    MuiTooltip: {
      defaultProps: {
        enterDelay: 500,
      },
      styleOverrides: {
        tooltip: {
          fontWeight: 400,
          backgroundColor: alpha(theme.blue, 0.8),
        },
      },
    },
  },
});

export const darkTheme = createTheme(muiTheme, {
  palette: {
    mode: 'dark',
  },
});
